import type { AnalyticsInterface } from './analytics';
import {
  maybeAddSegmentContext,
  maybeAddConsentOrigin,
  getSegmentIntegrations,
} from './analytics';
import type { SegmentLoadOptions } from './segmentConsent';
import { toSegmentLoadOptions } from './segmentConsent';
import type { EventType, GroupTrackTraits, Identity, Load, Config } from './types';

export type { SegmentLoadOptions };

// analytics is referenced by window.analytics at each instance below.
// Segment initially creates stub versions of analytics methods,
// queueing calls to these methods until the stubs are updated.

const hasAnalyticsFn = (name: string): boolean =>
  (<any>window).analytics &&
  (<any>window).analytics[name] &&
  typeof (<any>window).analytics[name] === 'function';

export const loadSegment = ({ key }: Load, options?: SegmentLoadOptions) => {
  if (hasAnalyticsFn('load')) {
    (<any>window).analytics.load(
      key,
      options ?? toSegmentLoadOptions((<any>window).Fides?.consent),
    );
  }
};

export type SegmentPage<T extends object> = { name?: string } & Config<T>;

export const pageSegment = <T extends object>({
  name,
  properties,
  options,
  callback,
}: SegmentPage<T>) => {
  if (hasAnalyticsFn('page')) {
    (<any>window).analytics.page(name, properties, options, callback);
  }
};

type Track<T extends object> = { event: string } & Config<T>;

export const trackSegment = <T extends object>({
  event,
  properties,
  options,
  callback,
}: Track<T>) => {
  if (hasAnalyticsFn('track')) {
    (<any>window).analytics.track(event, properties, options, callback);
  }
};

type Identify<T extends object> = { userId?: string } & Config<T>;

export const identifySegment = <T extends object>({
  userId,
  properties,
  options,
  callback,
}: Identify<T>) => {
  if (hasAnalyticsFn('identify')) {
    (<any>window).analytics.identify(userId, properties, options, callback);
  }
};

type Group<T extends object> = { groupId: string } & Config<T>;

export const segmentGroup = <T extends object>({
  groupId,
  properties,
  options,
  callback,
}: Group<T>) => {
  if (hasAnalyticsFn('group')) {
    (<any>window).analytics.group(groupId, properties, options, callback);
  }
};

export const resetSegment = () => {
  if (hasAnalyticsFn('reset')) {
    (<any>window).analytics.reset();
  }
};

export const readySegment = (callback: () => void) => {
  if (hasAnalyticsFn('ready')) {
    (<any>window).analytics.ready(callback);
  }
};

export const anonymousIdSegment = (callback: (anonymousId: string) => void) => {
  readySegment(() => {
    const anonymousId = (<any>window).analytics.user().anonymousId();
    if (anonymousId) {
      callback(anonymousId);
    }
  });
};

export const Analytics = {
  Make: (): AnalyticsInterface => {
    class Internal {
      track<T extends object>(mixedEvent: EventType<T>) {
        if (typeof mixedEvent === 'string') {
          console.warn(`Unhandled track event ${mixedEvent}`);
        } else {
          const context = maybeAddSegmentContext();
          const { event, ...properties } = mixedEvent;
          const consentProperties = maybeAddConsentOrigin({ ...properties });
          trackSegment({
            event,
            ...consentProperties,
            options: { ...context, ...getSegmentIntegrations() },
          });
        }
      }

      group<T extends object>({ event, ...properties }: GroupTrackTraits<T>) {
        segmentGroup({ event, ...properties });
      }

      identify<T extends object>(identity: Identity<T>) {
        const properties = maybeAddConsentOrigin(identity.properties);
        const context = maybeAddSegmentContext();
        const payload = {
          ...identity,
          properties,
          options: { ...context, ...getSegmentIntegrations() },
        };
        identifySegment(payload);
      }

      reset(): void {
        resetSegment();
      }

      load(load: Load, options?: SegmentLoadOptions): void {
        loadSegment(load, options);
      }

      page<T extends object>(page: SegmentPage<T>): void {
        const context = maybeAddSegmentContext();
        const properties = maybeAddConsentOrigin(page.properties);
        const payload = {
          ...page,
          properties,
          options: { ...context, ...getSegmentIntegrations() },
        };
        pageSegment(payload);
      }

      ready(callback: () => void): void {
        readySegment(callback);
      }

      anonymousId(callback: (anonymousId: string) => void): void {
        anonymousIdSegment(callback);
      }
    }
    return new Internal();
  },
};

export const makeAnalytics = () => Analytics.Make();

export const isSegmentInitialized = () => Boolean((<any>window).analytics?.initialized);
